import { watch } from 'vue'
import { WAYPOINTS_SOURCE } from '@/lib/enums'
import { controlState } from '@/services/control-layout-service'
import type { WebmapMapManager } from '@/composables/map-x/use-map-manager'

/**
 * *(AI-generated)*
 * Composable for controlling waypoint cluster rendering on map load and user settings change.
 */
export const useWaypointClustering = () => {
  /**
   * Toggle waypoint clustering on map load if needed
   * @param mapManager - A MapManager instance
   */
  const toggleClusteringOnLoad = (mapManager: WebmapMapManager) => {
    mapManager.events?.on('map-x-load', () => {
      toggleClustering(mapManager, controlState().waypointClustering)
    })
  }

  /**
   * Toggle waypoint clustering on the map based on user setting
   * @param mapManager - a MapManager instance
   */
  const toggleClusteringViaMapSetting = (mapManager: WebmapMapManager) => {
    watch(
      () => controlState().waypointClustering,
      (show: boolean) => toggleClustering(mapManager, show)
    )
  }

  const toggleClustering = (mapManager: WebmapMapManager, show: boolean) => {
    // Bypass — calls map directly to preserve all layers including those
    // added outside the stylesheet provider. Migrate to mapManager.stylesheet.*
    // once all layer/source management routes through the provider.
    const currentStyle = mapManager.viewer.map.getStyle()
    if (currentStyle) {
      const newStyle = {
        ...currentStyle,
        sources: {
          ...currentStyle.sources,
          [WAYPOINTS_SOURCE]: {
            ...currentStyle.sources[WAYPOINTS_SOURCE],
            cluster: show,
          },
        },
      }
      mapManager.viewer.map.setStyle(newStyle)
    }
  }

  return {
    toggleClusteringViaMapSetting,
    toggleClusteringOnLoad,
    toggleClustering,
  }
}
